// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Pure decision helpers for proactive JWT refresh.
 *
 * The 2026-07-27 incident: AuthContext only refreshed the token when the user
 * had been IDLE 15-20 minutes, so a continuously active user never refreshed,
 * aged out the 180-minute JWT mid-click, and was hard-logged-out. The fix keys
 * refresh on TOKEN AGE instead; these helpers hold that logic where jest can
 * reach it (house style: pure utils under src/utils, tests in __tests__).
 */

/**
 * Decode a JWT payload WITHOUT verification (display/scheduling only — the
 * backend re-verifies everything) and return its `exp` claim in MILLISECONDS
 * since the epoch. Returns null for a missing/garbled token or a missing/
 * non-numeric exp — callers treat null as "cannot schedule, let the 401 path
 * decide". Payload is base64url (RFC 7515): translate -_ before atob.
 */
export function getTokenExpiryMs(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' && Number.isFinite(decoded.exp)
      ? decoded.exp * 1000
      : null;
  } catch (_) {
    return null;
  }
}

/**
 * Should the app proactively refresh now? True when the token expires within
 * `refreshWindowMs` of `nowMs` (boundary inclusive — refreshing a minute early
 * is free, a minute late is a logout). An already-expired token also returns
 * true: the backend accepts recently-expired tokens on /api/refresh within its
 * grace window. Null expMs (unreadable token) returns false.
 */
export function shouldRefreshToken({ expMs, nowMs, refreshWindowMs }) {
  if (expMs == null || nowMs == null || refreshWindowMs == null) return false;
  return expMs - nowMs <= refreshWindowMs;
}
